import { toast } from 'sonner';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';
import api from '../api';

function EditContract() {
  const { loanId } = useParams();
  const [draft, setDraft] = useState(null);
  const [loan, setLoan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    fetchData();
  }, [loanId]);

  const fetchData = async () => {
    try {
      const loanRes = await api.get(`/api/loans/${loanId}`);
      setLoan(loanRes.data);
      
      // Create draft - backend handles "create or return existing"
      const draftRes = await api.post('/api/admin/contracts/draft', {
        loan_id: loanId,
        language: 'ca'
      });
      setDraft(draftRes.data);
    } catch (error) {
      console.error('Error loading draft:', error?.response?.data || error);
      toast.error(i18n.language === 'ca' ? 'Error al carregar el contracte' : 'Error al cargar el contrato');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Save only the editable fields of the draft
      await api.put(`/api/admin/contracts/draft/${draft._id}`, {
        contract_text: draft.contract_text,
        capital: draft.capital,
        interest_rate: draft.interest_rate,
        duration_months: draft.duration_months
      });
      
      // Also update the loan with new values to maintain consistency
      await api.put(`/api/admin/loans/${loan._id}`, {
        capital: draft.capital,
        interest_rate: draft.interest_rate,
        duration_months: draft.duration_months
      });
      
      // Reload loan data
      const loanRes = await api.get(`/api/loans/${loanId}`);
      setLoan(loanRes.data);
      
      toast.success(i18n.language === 'ca' ? 'Esborrany desat correctament' : 'Borrador guardado correctamente');
    } catch (error) {
      toast.error(i18n.language === 'ca' ? 'Error al desar' : 'Error al guardar');
      console.error('Save error:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleGenerate = async () => {
    if (!confirm(i18n.language === 'ca' 
      ? 'Generar contracte final amb aquestes condicions? Els emails s\'enviaran automàticament.'
      : '¿Generar contrato final con estas condiciones? Los emails se enviarán automáticamente.')) return;
    
    setGenerating(true);
    try {
      // IMPORTANT: Save draft first to ensure latest changes are in DB
      await api.put(`/api/admin/contracts/draft/${draft._id}`, {
        contract_text: draft.contract_text,
        capital: draft.capital,
        interest_rate: draft.interest_rate,
        duration_months: draft.duration_months
      });
      
      // Now finalize with the saved text
      await api.post(`/api/admin/contracts/draft/${draft._id}/finalize`);
      toast.success(i18n.language === 'ca' ? 'Contracte generat correctament' : 'Contrato generado correctamente');
      navigate('/admin/loans');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
      console.error('Generate error:', error);
    } finally {
      setGenerating(false);
    }
  };

  const updateField = (field, value) => {
    setDraft({ ...draft, [field]: value });
  };
  
  const updateNumericField = (field, value) => {
    setDraft({ ...draft, [field]: value });
  };
  
  const handleRegenerateText = async () => {
    setRegenerating(true);
    try {
      const newDraftRes = await api.post('/api/admin/contracts/draft', {
        loan_id: loanId,
        language: draft.language,
        capital: draft.capital,
        interest_rate: draft.interest_rate,
        duration_months: draft.duration_months,
        force_regenerate: true
      });
      setDraft(newDraftRes.data);
      toast.success(i18n.language === 'ca' ? 'Text regenerat des de plantilla' : 'Texto regenerado desde plantilla');
    } catch (error) {
      console.error('Error regenerating contract:', error);
      toast.error(t('errors.unknown'));
    } finally {
      setRegenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="main-layout">
        <Sidebar />
        <div className="content-area" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-layout">
      <Sidebar />
      <div className="content-area">
        <TopBar 
          title={t('contract.editTitle')}
          actions={
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                onClick={handleSave}
                disabled={saving}
                className="btn-secondary"
                style={{ padding: '0.75rem 1.5rem' }}
              >
                {saving ? t('status.saving') : t('buttons.saveChanges')}
              </button>
              <button
                onClick={handleGenerate}
                disabled={generating}
                className="btn-primary"
                style={{ padding: '0.75rem 1.5rem' }}
              >
                {generating ? t('contract.generating') : t('contract.generateFinal')}
              </button>
            </div>
          }
        />

        <main style={{ padding: '2.5rem' }} className="fade-in">
          {/* Info Card */}
          <div className="fintech-card" style={{ marginBottom: '2rem', background: 'linear-gradient(135deg, #103766 0%, #1a4d7a 100%)', color: 'white' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '0.5rem' }}>
                  {i18n.language === 'ca' ? 'Préstec' : 'Préstamo'} #{loan?._id?.slice(-8)}
                </h3>
                <p style={{ opacity: 0.9 }}>
                  {i18n.language === 'ca' ? 'Idioma del contracte' : 'Idioma del contrato'}: 
                  <strong style={{ marginLeft: '0.5rem' }}>{draft?.language === 'ca' ? 'Català 🇨🇦' : 'Español 🇪🇸'}</strong>
                </p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '2rem', fontWeight: '700' }}>€{draft?.capital?.toLocaleString()}</div>
                <div style={{ opacity: 0.9 }}>{draft?.interest_rate}% • {draft?.duration_months} {i18n.language === 'ca' ? 'mesos' : 'meses'}</div>
                
                {/* Language Selector for Contract */}
                <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                  <button
                    onClick={async () => {
                      setRegenerating(true);
                      try {
                        const newDraft = await api.post('/api/admin/contracts/draft', {
                          loan_id: loanId,
                          language: 'ca',
                          capital: draft.capital,
                          interest_rate: draft.interest_rate,
                          duration_months: draft.duration_months,
                          force_regenerate: true
                        });
                        setDraft(newDraft.data);
                      } finally { setRegenerating(false); }
                    }}
                    style={{
                      padding: '0.5rem 1rem',
                      background: draft?.language === 'ca' ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.1)',
                      border: draft?.language === 'ca' ? '2px solid white' : '1px solid rgba(255,255,255,0.3)',
                      borderRadius: '6px',
                      color: 'white',
                      fontSize: '0.875rem',
                      cursor: 'pointer',
                      fontWeight: draft?.language === 'ca' ? '600' : '400'
                    }}
                  >
                    CA
                  </button>
                  <button
                    onClick={async () => {
                      setRegenerating(true);
                      try {
                        const newDraft = await api.post('/api/admin/contracts/draft', {
                          loan_id: loanId,
                          language: 'es',
                          capital: draft.capital,
                          interest_rate: draft.interest_rate,
                          duration_months: draft.duration_months,
                          force_regenerate: true
                        });
                        setDraft(newDraft.data);
                      } finally { setRegenerating(false); }
                    }}
                    style={{
                      padding: '0.5rem 1rem',
                      background: draft?.language === 'es' ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.1)',
                      border: draft?.language === 'es' ? '2px solid white' : '1px solid rgba(255,255,255,0.3)',
                      borderRadius: '6px',
                      color: 'white',
                      fontSize: '0.875rem',
                      cursor: 'pointer',
                      fontWeight: draft?.language === 'es' ? '600' : '400'
                    }}
                  >
                    ES
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Edit Fields */}
          <div className="fintech-card" style={{ marginBottom: '2rem', position: 'relative' }}>
            {regenerating && (
              <div style={{ 
                position: 'absolute', 
                top: 0, 
                left: 0, 
                right: 0, 
                bottom: 0, 
                background: 'rgba(0,0,0,0.7)', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                borderRadius: '20px',
                zIndex: 10
              }}>
                <div style={{ color: 'white', fontSize: '1rem', fontWeight: '600' }}>
                  ⚡ {i18n.language === 'ca' ? 'Actualitzant contracte...' : 'Actualizando contrato...'}
                </div>
              </div>
            )}
            <h3 style={{ fontSize: '1.1rem', fontWeight: '700', color: '#60a5fa', marginBottom: '1rem' }}>
              ⚡ {i18n.language === 'ca' ? 'Edició Ràpida de Valors' : 'Edición Rápida de Valores'}
            </h3>
            <p style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)', marginBottom: '1rem' }}>
              {i18n.language === 'ca' 
                ? 'Modifica aquests valors i es reflectiran automàticament al text del contracte:'
                : 'Modifica estos valores y se reflejarán automáticamente en el texto del contrato:'}
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: '600', color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem' }}>
                  {t('labels.capital')}
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={draft?.capital || ''}
                  onChange={(e) => updateNumericField('capital', parseFloat(e.target.value))}
                  style={{ 
                    width: '100%', fontSize: '1.1rem', fontWeight: '700',
                    padding: '0.7rem', border: '1.5px solid rgba(255,255,255,0.15)',
                    borderRadius: '10px', color: 'white', background: 'rgba(255,255,255,0.06)',
                    boxSizing: 'border-box', outline: 'none'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: '600', color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem' }}>
                  {t('labels.interestRate')}
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={draft?.interest_rate || ''}
                  onChange={(e) => updateNumericField('interest_rate', parseFloat(e.target.value))}
                  style={{ 
                    width: '100%', fontSize: '1.1rem', fontWeight: '700',
                    padding: '0.7rem', border: '1.5px solid rgba(255,255,255,0.15)',
                    borderRadius: '10px', color: '#34d399', background: 'rgba(255,255,255,0.06)',
                    boxSizing: 'border-box', outline: 'none'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: '600', color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem' }}>
                  {t('labels.duration')}
                </label>
                <input
                  type="number"
                  value={draft?.duration_months || ''}
                  onChange={(e) => updateNumericField('duration_months', parseInt(e.target.value))}
                  style={{ 
                    width: '100%', fontSize: '1.1rem', fontWeight: '700',
                    padding: '0.7rem', border: '1.5px solid rgba(255,255,255,0.15)',
                    borderRadius: '10px', color: '#fb923c', background: 'rgba(255,255,255,0.06)',
                    boxSizing: 'border-box', outline: 'none'
                  }}
                />
              </div>
            </div>
            <button
              onClick={handleRegenerateText}
              disabled={regenerating}
              className="btn-ghost"
              data-testid="regenerate-text-btn"
              style={{ 
                marginTop: '1rem', padding: '0.6rem 1.2rem', fontSize: '0.85rem',
                border: '1px solid rgba(96,165,250,0.4)', color: '#60a5fa'
              }}
            >
              {regenerating 
                ? (i18n.language === 'ca' ? 'Regenerant...' : 'Regenerando...')
                : (i18n.language === 'ca' ? 'Regenerar text des de plantilla' : 'Regenerar texto desde plantilla')}
            </button>
            <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)', marginTop: '0.5rem', margin: '0.5rem 0 0 0' }}>
              {i18n.language === 'ca' 
                ? 'Atenció: regenerar sobreescriurà les edicions manuals del text'
                : 'Atención: regenerar sobreescribirá las ediciones manuales del texto'}
            </p>
          </div>
          <div className="fintech-card" style={{ padding: '1.25rem' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: '700', color: '#60a5fa', margin: 0 }}>
                📄 {i18n.language === 'ca' ? 'Text Complet del Contracte' : 'Texto Completo del Contrato'}
              </h3>
              <span style={{ fontSize: '0.875rem', color: '#616161' }}>
                {i18n.language === 'ca' 
                  ? 'Edita el text directament per personalitzar el contracte'
                  : 'Edita el texto directamente para personalizar el contrato'}
              </span>
            </div>
            
            <textarea
              value={draft?.contract_text || ''}
              onChange={(e) => updateField('contract_text', e.target.value)}
              rows={40}
              style={{
                width: '100%',
                padding: '1rem',
                border: '1.5px solid rgba(255,255,255,0.15)',
                borderRadius: '10px',
                fontSize: '0.85rem',
                lineHeight: '1.8',
                fontFamily: "'Inter', sans-serif",
                color: 'rgba(255,255,255,0.9)',
                backgroundColor: 'rgba(255,255,255,0.06)',
                resize: 'vertical',
                boxSizing: 'border-box',
                outline: 'none'
              }}
              placeholder={i18n.language === 'ca' 
                ? 'El text del contracte apareixerà aquí...'
                : 'El texto del contrato aparecerá aquí...'}
            />
            
            <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'rgba(59,130,246,0.1)', borderRadius: '8px', border: '1px solid rgba(59,130,246,0.3)' }}>
              <p style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.7)', margin: 0 }}>
                💡 <strong>{i18n.language === 'ca' ? 'Consell' : 'Consejo'}:</strong> {i18n.language === 'ca' 
                  ? 'Pots editar qualsevol part del text. Els canvis als valors numèrics es reflecteixen automàticament.'
                  : 'Puedes editar cualquier parte del texto. Los cambios en los valores numéricos se reflejan automáticamente.'}
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', marginTop: '1.5rem' }}>
            <button
              onClick={() => navigate('/admin/loans')}
              className="btn-ghost"
              style={{ padding: '0.75rem 1.5rem' }}
            >
              ← {t('common.back')}
            </button>
            
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                onClick={handleSave}
                disabled={saving}
                className="btn-secondary"
                style={{ padding: '1rem 2rem' }}
              >
                {saving ? t('status.saving') : t('buttons.saveDraft')}
              </button>
              <button
                onClick={handleGenerate}
                disabled={generating}
                className="btn-primary"
                style={{ padding: '1rem 2rem' }}
              >
                {generating ? t('contract.generating') : t('contract.generateFinal')}
              </button>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default EditContract;
